import React, { FC } from 'react';
import { createFilter, OptionTypeBase } from 'react-select';
import { CustomField, Select } from 'react-uforms';
import { WithStyle } from '@ch/common/utils';
import { FormSelectProps } from './types';
import { ColorMarkStyled, FormMobileSelectStyled, FormSelectStyled, FormSelectWrapStyled } from './select.styled';
import {
  ClearIndicator,
  DropdownIndicator,
  MultiValueRemove,
  NoOptionsMessage,
  OptionBasic,
  OptionMultiple,
} from './select-elements';
import { IconChevronDown } from '@ch/common/components/icon';
import CustomMenuList from './virtual-list';

export const getFormattedValue = (inputValue: string, options: OptionTypeBase[]): OptionTypeBase => {
  const index = options.findIndex(option => option.value === inputValue);
  return index !== -1 ? options[index] : { label: inputValue, value: inputValue };
};
export const formatDefaultValues = (inputValue: string[] | string, options: any): OptionTypeBase[] | OptionTypeBase => {
  if (Array.isArray(inputValue)) {
    return inputValue.map(item => getFormattedValue(item, options));
  }
  return getFormattedValue(inputValue, options);
};

export const FormSelect: FC<FormSelectProps> & WithStyle = ({ onBlur, ...props }) => {
  const {
    name,
    disabled,
    required,
    options,
    isMulti,
    isClearable,
    instanceId,
    onChange,
    onFocus,
    placeholder,
    location,
    isMobile,
    size,
    hideError,
    menuPlacement,
    colorMark: initColorMark,
    isSearchable,
    'data-test': dataTest,
    'data-test-id': dataTestId,
    onInputChange,
    isLoading,
  } = props;
  const colorMark = initColorMark && (initColorMark.includes('#') ? initColorMark : `#${initColorMark}`);
  const Option = (isMulti && OptionMultiple) || OptionBasic;
  const mobileOptions = (newOptions: any): any => {
    const optionsWithPlaceholder =
      placeholder && isClearable ? [{ label: placeholder, name: placeholder, value: null }, ...newOptions] : newOptions;
    if (Array.isArray(optionsWithPlaceholder)) {
      return optionsWithPlaceholder.map(o => ({
        name: o.label,
        value: o.value,
        disabled: o.disabled,
      }));
    } else {
      return [{ name: optionsWithPlaceholder.label, value: optionsWithPlaceholder.value }];
    }
  };
  const getMobileValue = (value: string, newOptions: any, placeholder: any) => {
    if (!value) {
      return { label: placeholder || null, value: null };
    }
    const label = Array.isArray(newOptions) ? newOptions.find(o => o.value === value).label : newOptions.label;
    return { label, value };
  };
  if (isMobile) {
    return (
      <FormMobileSelectStyled {...{ hideError, colorMark }} data-test={dataTest} data-test-id={dataTestId}>
        {colorMark && <ColorMarkStyled />}
        <IconChevronDown $size={8} $color="black" />
        <Select
          multiple={isMulti}
          options={mobileOptions(options)}
          onChange={(event, action?: any) => {
            if (onChange) {
              onChange(
                getMobileValue(JSON.parse((event.target as HTMLTextAreaElement).value)['v'], options, placeholder),
                action,
              );
            }
          }}
          {...{ name, required, disabled, size, onBlur, hideError, onInputChange }}
        />
      </FormMobileSelectStyled>
    );
  }
  return (
    <CustomField name={name} hideError={hideError}>
      {({ getValue, setValue, getErrors, setTouched }) => {
        return (
          <FormSelectWrapStyled data-test={dataTest} data-test-id={dataTestId} $disabled={disabled}>
            <FormSelectStyled
              components={{
                DropdownIndicator,
                ClearIndicator,
                NoOptionsMessage,
                Option,
                MultiValueRemove,
                MenuList: CustomMenuList,
              }}
              isDisabled={disabled}
              isCreatable={false}
              value={getValue() ? formatDefaultValues(getValue(), options) : null}
              hideSelectedOptions={false}
              menuPlacement={menuPlacement || 'auto'}
              onChange={(newValue: any, action: any) => {
                let value = undefined;
                if (newValue) {
                  value = Array.isArray(newValue) ? newValue.map(item => item.value) : newValue.value;
                }
                setValue(value, () => {
                  if (onChange) {
                    onChange(newValue, action);
                  }
                });
              }}
              onBlur={(event: any) => {
                if (onBlur) {
                  onBlur(event);
                }
                setTouched();
              }}
              captureMenuScroll={false}
              filterOption={createFilter({ ignoreAccents: false })}
              className={`react-select-container ${location ? 'react-select-location' : ''}`}
              classNamePrefix="react-select"
              instanceId={instanceId || name}
              error={Array.isArray(getErrors()) && getErrors().length > 0}
              {...{
                name,
                required,
                options,
                isMulti,
                placeholder,
                isClearable,
                colorMark,
                isSearchable,
                onFocus,
                onInputChange,
                isLoading,
              }}
            />
          </FormSelectWrapStyled>
        );
      }}
    </CustomField>
  );
};

FormSelect.defaultProps = {
  hideError: false,
  isMulti: false,
};

FormSelect.displayName = 'FormSelect';
